"use client";

import React, { useEffect, useRef, useState } from "react";
import { MapPin, Phone, Mail, ArrowUpRight, Clock } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.1,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const officeHours = {
  days: "Monday – Sunday",
  hours: "10:00 AM - 9:00 PM"
};
export default function OurOffices() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll();
  const {
    ref: tableRef,
    isVisible: isTableVisible
  } = useRevealOnScroll();
  return <section data-section-id="234928" id="find-us-at-yonge-sheppard" className="bg-background text-foreground py-24 lg:py-32 overflow-hidden">
      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        {}
        <div ref={headerRef} className={`mb-16 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-4xl md:text-5xl font-default font-bold text-foreground tracking-tight mb-4">
            Find Us at Yonge & Sheppard
          </h2>
          <div className="w-24 h-1 bg-chart-1"></div>
        </div>

        {}
        <div ref={gridRef} className={`grid grid-cols-1 lg:grid-cols-2 gap-0 border border-border transition-all duration-1000 delay-200 ease-out ${isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {}
          <div className="relative h-[400px] lg:h-auto bg-muted border-b lg:border-b-0 lg:border-r border-border overflow-hidden group">
            <img alt="Map showing Yonge Sheppard RMT & Facial Wellness location in North York" className="absolute inset-0 w-full h-full object-cover contrast-100 opacity-90 transition-transform duration-1000 group-hover:scale-105" src="https://yswellness.ca/wp-content/uploads/2026/07/IMG_7293-1-scaled.jpg" />
            {}
            <div className="absolute inset-0 bg-background/5 mix-blend-multiply pointer-events-none"></div>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center">
              <div className="relative">
                <div className="absolute inset-0 bg-chart-1 rounded-lg animate-ping opacity-20"></div>
                
              </div>
              <div className="mt-3 bg-background/95 backdrop-blur-sm border border-border px-4 py-2 shadow-sm rounded-md">
                
              </div>
            </div>
          </div>

          {}
          <div className="flex flex-col justify-center p-8 md:p-16 bg-card">
            <div className="space-y-12">
              {}
              <div className="flex items-start gap-6">
                <div className="p-3 bg-accent border border-border shrink-0 rounded-lg">
                  <MapPin className="w-6 h-6 text-chart-1" />
                </div>
                <div>
                  <h3 className="text-xs font-bold tracking-widest uppercase text-muted-foreground mb-3">
                    Clinic Location
                  </h3>
                  <p className="text-xl md:text-2xl font-default font-medium text-foreground leading-snug">1 Elmhurst Ave, North York, ON M2N 1R3</p>
                  <Button variant="link" className="p-0 h-auto mt-4 text-chart-1 font-medium hover:text-chart-1/80" asChild>
                    <Link to="https://maps.google.com" newTab>
                      Get Directions <ArrowUpRight className="w-4 h-4 ml-1" />
                    </Link>
                  </Button>
                </div>
              </div>

              {}
              <div className="flex items-start gap-6">
                <div className="p-3 bg-accent border border-border shrink-0 rounded-lg">
                  <Phone className="w-6 h-6 text-chart-1" />
                </div>
                <div>
                  <h3 className="text-xs font-bold tracking-widest uppercase text-muted-foreground mb-3">
                    Call Now
                  </h3>
                  <p className="text-xl md:text-2xl font-default font-medium text-foreground">
                    416-938-9900
                  </p>
                </div>
              </div>

              {}
              <div className="flex items-start gap-6">
                <div className="p-3 bg-accent border border-border shrink-0 rounded-lg">
                  <Mail className="w-6 h-6 text-chart-1" />
                </div>
                <div>
                  <h3 className="text-xs font-bold tracking-widest uppercase text-muted-foreground mb-3">
                    Official Email
                  </h3>
                  <p className="text-lg md:text-xl font-default font-medium text-foreground break-all">
                    hello@yswellness.ca
                  </p>
                  <Button variant="link" className="p-0 h-auto mt-4 text-chart-1 font-medium hover:text-chart-1/80" asChild>
                    <Link to="/ContactPage">
                      Send Inquiry <ArrowUpRight className="w-4 h-4 ml-1" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {}
        <div ref={tableRef} className={`mt-24 transition-all duration-1000 delay-400 ease-out ${isTableVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="flex items-center gap-3 mb-8">
            <Clock className="w-6 h-6 text-chart-1" />
            <h3 className="text-2xl font-default font-bold text-foreground">
              Wellness Hours
            </h3>
          </div>
          
          <div className="bg-card border border-border rounded-xl p-8 md:p-12 shadow-sm">
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">
              {officeHours.days}
            </p>
            <p className="text-2xl md:text-3xl font-default font-medium text-foreground">
              {officeHours.hours}
            </p>
          </div>
        </div>
      </div>
    </section>;
}